/* Built In Imports */

import { usePathname } from 'next/navigation';

/* External Imports */
import {
  Box,
  Flex,
  Image,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import { ProgramStructureSt } from '@ieo/Common/CompLangConfig';

import { usePageConfigValue } from '@ieo/hooks/usePageConfigVal';

/* Services */
/* Styles */

/**
 *
 * @param {Object} SectionContent
 * @param {String} region
 * @param {String} lang
 * @returns {ReactElement} ProgramStructure
 */

const ProgramStructureV8 = ({
  sectionContent,
  region,
  lang,
  pageName,
  pageData,
  prgData,
}) => {
  // console.log('sec content in program structure ====>', sectionContent);
  const path = usePathname();

  const currentCoupon = Object.values(prgData)[0]?.coupon;

  const { pageConfigVal, isThemeActive } = usePageConfigValue(
    pageData,
    region,
    currentCoupon
  );

  return (
    <Box
      background={
        isThemeActive && pageConfigVal
          ? `url(${pageConfigVal?.prgStructure?.bgImage?.bigScreen}) repeat-x #F1EBDF`
          : `#F1EBDF`
      }
      // bgRepeat="repeat-x"
      bgPos={{ base: 'inherit', md: 'top' }}
      bgSize={{ base: 'auto 50px', md: 'auto 10%', xl: 'auto 15%' }}
    >
      <Flex
        maxW={1030}
        pt={{ base: '30px', md: '40px', xl: '80px' }}
        margin={'0 auto'}
      >
        <Box
          w="full"
          mx={{ base: '1rem', xl: 'auto' }}
          pb={{ base: '10px', md: '50px' }}
        >
          <Flex alignItems={{ sm: 'center' }} justify="center">
            <Box
              as="h1"
              lineHeight={{ base: '42px', lg: 'initial' }}
              pb={{ base: '18px', md: '26px' }}
              {...ProgramStructureSt[lang]?.h2}
              textAlign="center"
            >
              {sectionContent?.[0]?.titleText}
            </Box>
          </Flex>

          <Tabs
            position="relative"
            variant="unstyled"
            display={{ base: 'block', lg: 'none' }}
          >
            <TabList>
              <Tab _selected={{ color: '#12166F' }} color="#414141">
                {' '}
                <Text
                  as="h4"
                  fontSize={{ base: '16px', lg: '28px' }}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  px={{ base: '0', lg: '100px' }}
                  pb={{ base: '10px', lg: '30px' }}
                >
                  {sectionContent[1]?.titleText}
                </Text>
              </Tab>
              <Tab
                _selected={{ color: '#12166F' }}
                color="#414141"
                p={{ base: '0px', md: '10px' }}
              >
                <Text
                  as="h4"
                  fontSize={{ base: '16px', lg: '28px' }}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  px={{ base: 0, lg: '100px' }}
                  pb={{ base: '10px', lg: '30px' }}
                >
                  {sectionContent[3]?.titleText}
                </Text>
              </Tab>
            </TabList>
            <TabIndicator
              mt="-1.5px"
              height="2px"
              bg="blue.500"
              borderRadius="1px"
            />
            <TabPanels>
              <TabPanel px={{ base: '10px' }}>
                {sectionContent[2]?.cards?.map(card => {
                  return (
                    <ProgramStructureCard
                      key={nanoid(4)}
                      lang={lang}
                      data={card}
                    />
                  );
                })}
              </TabPanel>
              <TabPanel px={{ base: '10px' }}>
                {sectionContent[4]?.cards?.map(card => {
                  return <ProgramStructureCard key={nanoid(4)} data={card} />;
                })}
              </TabPanel>
            </TabPanels>
          </Tabs>
          <Box mt="34px" display={{ base: 'none', lg: 'block' }}>
            <Flex
              justifyContent="space-between"
              gap={{ base: '20px', lg: '90px' }}
            >
              <Flex
                flexWrap="wrap"
                mt={{ base: '0', md: '14.4px' }}
                w={{ base: '100%', lg: '50%' }}
                alignItems="flex-start"
              >
                <Text
                  as="h4"
                  color="#12166F"
                  fontSize={{ base: '16px', lg: '28px' }}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  px={{ base: '0', lg: '40px' }}
                  pb={{ base: '10px', lg: '30px' }}
                  maxW="430px"
                >
                  {sectionContent[1]?.titleText}
                </Text>
                {sectionContent[2]?.cards?.map(card => {
                  return (
                    <ProgramStructureCard
                      key={nanoid(4)}
                      lang={lang}
                      data={card}
                    />
                  );
                })}
              </Flex>
              <Flex
                flexWrap="wrap"
                mt={{ base: '0', md: '14.4px' }}
                w={{ base: '100%', lg: '50%' }}
                alignItems="flex-start"
              >
                <Text
                  as="h4"
                  color="#12166F"
                  fontSize={{ base: '16px', lg: '28px' }}
                  fontFamily="FedraSansStd-medium"
                  textAlign="center"
                  pb={{ base: '10px', lg: '30px' }}
                >
                  {sectionContent[3]?.titleText}
                </Text>
                {sectionContent[4]?.cards?.map(card => {
                  return <ProgramStructureCard key={nanoid(4)} data={card} />;
                })}
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

const ProgramStructureCard = ({ data }) => {
  return (
    <Flex
      alignItems="flex-start"
      gap={{ base: '20px', lg: '26px' }}
      py={{ lg: '10px' }}
      mb="10px"
    >
      <Image
        width={{ base: '22px', lg: '31px' }}
        height={{ base: '23px', lg: '33px' }}
        src={data.thumbnail.url}
        alt={data.thumbnail.alt || ''}
      />
      <Text
        as="h4"
        color="#414141"
        fontSize={{ base: '16px', lg: '22px' }}
        fontFamily="FedraSansStd-book"
      >
        {data.title}
      </Text>
    </Flex>
  );
};

export default ProgramStructureV8;
